import Vue from 'vue'
import HighchartsCore from 'highcharts'
import stockInit from 'highcharts/modules/stock'
import HighchartsNoData from 'highcharts/modules/no-data-to-display'
import Drilldown from 'highcharts/modules/drilldown'
import VueHighcharts from 'highcharts-vue'
import Extremes from './plugins/Extremes'
import GradientAreaSeries from './plugins/GradientAreaSeries'
import CreditsExtension from './plugins/CreditsExtension'
import RangeSelectorExtension from './plugins/RangeSelectorExtension'
import AutoColor from './plugins/AutoColor'
import Presets from './plugins/Presets'
import LinkData from './plugins/LinkData'
import Performance from './plugins/Performance'
import RealtimeData from "./plugins/RealtimeData"
import colors from 'fincraft-lib-basics/src/js/colors'

window.Highcharts = HighchartsCore

const DATEFORMATNOYEAR = '%A %d. %b'
const DATEFORMAT = DATEFORMATNOYEAR + ' %Y'
const TIMEFORMAT = '%H:%M'
const DATEFORMATWITHTIME = DATEFORMATNOYEAR + ', ' + TIMEFORMAT

/** data grouping definition */
const dataGrouping = {
  /** display last value of the group */
  approximation: function (groupData) {
    return groupData[groupData.length - 1] // return last
  },

  /** date formatting of the grouped data. For each unit, define 3 formats:
   * first format: when the data is grouped for ONE unit (for example: ONE hour);
   * second and third formats: when the data is grouped for more than one unit (for example: TWO hours),
   * we need to define the formats for the 'from' and for the 'to' values
   * (for example: hour: [DATEFORMATWITHTIME, DATEFORMATWITHTIME, ' - ' + TIMEFORMAT]
   * would mean that: 1) when the data is grouped for ONE hour, it will be displayed as DATEFORMATWITHTIME.
   * When more than ONE hours are grouped, 'from' is displayed as 'DATEFORMATWITHTIME' and
   * 'to' is displayed as ' - TIMEFORMAT') */
  dateTimeLabelFormats: {
    millisecond: [DATEFORMATWITHTIME + ':%S.%L', DATEFORMATWITHTIME + ':%S.%L', TIMEFORMAT + ':%S.%L'],
    second: [DATEFORMATWITHTIME + ':%S', DATEFORMATWITHTIME + ':%S', ' - ' + TIMEFORMAT + ':%S.%L'],
    minute: [DATEFORMATWITHTIME, DATEFORMATWITHTIME, ' - ' + TIMEFORMAT + ':%S'],
    hour: [DATEFORMATWITHTIME, DATEFORMATWITHTIME, ' - ' + TIMEFORMAT],
    day: [DATEFORMAT],
    week: ['Woche von ' + DATEFORMAT, 'Wochen von ' + DATEFORMAT, ' - ' + DATEFORMAT],
    month: ['%B %Y'],
    year: ['%Y']
  },
  units: [
    ['hour', [1]],
    ['day', [1]],
    ['week', [1]]
  ]
}


Object.assign(HighchartsCore.defaultOptions.lang, {
  loading: 'Lädt...',
  months: [
    'Januar', 'Februar', 'März',
    'April', 'Mai', 'Juni',
    'Juli', 'August', 'September',
    'Oktober', 'November', 'Dezember'
  ],
  shortMonths: [
    'Jan.', 'Feb.', 'März',
    'Apr.', 'Mai', 'Juni',
    'Juli', 'Aug.', 'Sep.',
    'Okt.', 'Nov.', 'Dez.'
  ],
  weekdays: [
    'Sonntag', 'Montag', 'Dienstag',
    'Mittwoch', 'Donnerstag', 'Freitag',
    'Samstag'
  ],
  decimalPoint: ',',
  numericSymbols: [
    'k',
    'M',
    'G',
    'T',
    'P',
    'E'
  ],
  resetZoom: 'Zoom zurücksetzen',
  resetZoomTitle: 'Zoom auf 1:1 zurücksetzen',
  thousandsSep: '.',
})

// Highcharts init
// exportingInit(HighchartsCore)
stockInit(HighchartsCore)
HighchartsNoData(HighchartsCore)
Drilldown(HighchartsCore)
Extremes(HighchartsCore)
AutoColor(HighchartsCore)
GradientAreaSeries(HighchartsCore)
CreditsExtension(HighchartsCore)
RangeSelectorExtension(HighchartsCore)
Presets(HighchartsCore)
LinkData(HighchartsCore)
Performance(HighchartsCore)
RealtimeData(HighchartsCore)

const refDate = new Date()

export const DEFAULT_CHART_OPTIONS = {
  colors: [colors['french-blue'], '#0d233a', '#8bbc21', '#910000', '#1aadce',
    '#492970', '#f28f43', '#77a1e5', '#c42525', '#a6c96a'],
  chart: {
    backgroundColor: null,
    style: { //Default style
      fontFamily: 'Gilroy'
    },
    marginBottom: 60,
  },
  lang: {
    noData: "Keine verfügbare Daten",
  },
  noData: {
    style: {
      //fontWeight: 'bold', fontSize: '15px',  color: '#303030' //TODO all styles and fonts
    }
  },
  scrollbar: {
    enabled: false
  },
  navigator: {
    enabled: false
  },
  legend:{
    y: 10,
  },
  credits: {
    enabled: true,
    href: 'https://www.vwd.com/infront/',
    text: 'Quelle: Infront',
    position: {
      y: -15
    }
  },
  title: {
    style: {
      'font-size': '20px', //TODO all styles and fonts
    },
    text: "",
  },
  time: {
    timezoneOffset: refDate.getTimezoneOffset(),
  },
  tooltip: {
    xDateFormat: DATEFORMATWITHTIME,
    outside: false,
    backgroundColor: 'var(--white)',
    borderWidth: 0,
    enabled: true,
    style: {
      color: 'var(--night-blue)',
      fontFamily: 'Gilroy',
      // TODO find a way to be able to set a styling globally for xs and another for other breakpoints (here fontSize
      // 12px for xs, 16px for the others)
      fontSize: '12px',
      fontWeight: 'bold'
    }
  },
  plotOptions: {
    area: {
      threshold: null, //important, that the area dont need to start at 0
      turboThreshold:0,
      dataGrouping,
    },
    line: {
      dataGrouping
    },
    series: {
      turboThreshold:0,
    }
  },
  yAxis: {
    lineWidth: 1
  },
  rangeSelector: {
    labelStyle: {
      display: 'none',
    },
    inputEnabled: false,
    buttonPosition: {
      align: 'right'
    },
    buttons: [
      {
        type: 'day',
        count: 1,
        textDropDown: 'letzter Tag',
        textLong: '1 Tag',
        text: '1T',
        dataGrouping: {
          forced: true,
          units: [
            ['second', [1]],
            ['minute', [1]],
            ['hour', [1]],
          ],
        },
      },
      {
        type: 'week',
        count: 1,
        textDropDown: 'letzte Woche',
        textLong: '1 Woche',
        text: '1W',
        finTimeSeriesSuffix: '1W',
        dataGrouping: {
          forced: true,
          units: [
            ['hour', [1]],
            ['day', [1]],
          ],
        }
      },
      {
        type: 'month',
        count: 1,
        textDropDown: 'letzter Monat',
        textLong: '1 Monat',
        text: '1M',
        finTimeSeriesSuffix: '1M',
        dataGrouping: {
          forced: true,
          units: [
            ['hour', [1]],
            ['day', [1]],
            ['week', [1]]
          ],
        }
      },
      {
        type: 'month',
        count: 3,
        textDropDown: 'letzten 3 Monate',
        textLong: '3 Monaten',
        text: '3M',
        finTimeSeriesSuffix: '3M',
        dataGrouping: {
          forced: true,
          units: [
            ['day', [1]],
            ['week', [1]]
          ],
        }
      },
      {
        type: 'month',
        count: 6,
        textDropDown: 'letzten 6 Monate',
        textLong: '6 Monaten',
        text: '6M',
        finTimeSeriesSuffix: '6M',
        dataGrouping: {
          forced: true,
          units: [
            ['day', [1]],
            ['week', [1]]
          ],
        }
      },
      {
        type: 'year',
        count: 1,
        textDropDown: 'letztes Jahr',
        textLong: '1 Jahr',
        text: '1J',
        finTimeSeriesSuffix: '1Y',
        dataGrouping: {
          forced: true,
          units: [
            ['day', [1]],
            ['week', [1]]
          ]
        }
      },
      {
        type: 'all',
        textDropDown: 'alle Daten',
        textLong: 'Start',
        text: 'MAX',
        dataGrouping: {
          forced: true,
          units: [
            ['hour', [1]],
            ['day', [1]],
            ['week', [1]]
          ],
        }
      }
    ],
    buttonTheme: { // styles for the buttons
      fill: 'none',
      stroke: 'rgb(245, 248, 249)',
      'stroke-width': 1,
      width: 42,
      style: {
        color: 'var(--night-blue)',
        fontWeight: 'bold'
      },
      states: {
        hover: {},
        select: {
          fill: 'var(--night-blue)',
          style: {
            color: 'white'
          }
        }
        // disabled: { ... }
      }
    },
  },
}
HighchartsCore.setOptions(DEFAULT_CHART_OPTIONS)


Vue.use(VueHighcharts, {HighchartsCore})
